import {
  Button,
  Loading,
  Sizes,
  TextInput,
} from '@sede-enterprise/shell-ds-react-framework';
import { useAppUserDetailsFromJwt } from 'common-react/auth/utils/hooks/useAppUserDetailsFromJwt/useAppUserDetailsFromJwt';
import { companyListAnimation } from 'components/CompanyList/animation-parameters';
import {
  CompanyListScrollContainer,
  CompanyListWrapper,
  CompanyLoaderWrapper,
} from 'components/CompanyList/CompanyList.styled';
import { CompanyListDataNode } from 'components/CompanyList/CompanyList.types';
import CompanyListItem from 'components/CompanyList/CompanyListItem';
import { NoDataFound } from 'components/CompanyList/NoDataFound';
import { CompanyListContextProps } from 'components/NavBar/components/CompanySelector/CompanySelectorModal/CompanySelector.types';
import {
  CompanyModalWrapper,
  ErrorNotice,
  LoadMoreButtonContainer,
} from 'components/NavBar/components/CompanySelector/CompanySelectorModal/CompanySelectorModal.styled';
import { prefetchSites } from 'components/NavBar/components/CompanySelector/CompanySelectorModal/prefetch-sites';
import { GET_COMPANIES_AND_SITES } from 'components/NavBar/components/CompanySelector/CompanySelectorModal/queries';
import { getDefaultPathForUser } from 'components/NavBar/NavBar';
import { DYNATRACE_ACTIONS } from 'constants/dynatrace-actions';
import { Permissions } from 'context/constants';
import { usePlutoApiQuery } from 'hooks/useQuery/plutoApi';
import React, { createContext, useEffect, useRef, useState } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { useStore } from 'store';
import { dynatraceTrackInstantAction } from 'utils/dynatrace';
import { extractFirstSegmentFromUrl } from 'utils/url';
import { useDebounce } from 'utils/useDebounce';

const DEFAULT_LOAD_COUNT = 20;

export const CompanyListContext = createContext<CompanyListContextProps>({
  loadingSites: false,
  selected: undefined,
});

export const CompanySelectorModal = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [displayedRecords, setDisplayedRecords] = useState<Array<any>>([]);
  const { setCompanyData, currentDate, setCompanyModalClosed, appTheme } =
    useStore((state) => ({
      setCompanyData: state.setCompanyData,
      currentDate: state.currentDate,
      setCompanyModalClosed: state.setCompanyModalClosed,
      appTheme: state.appTheme,
    }));
  const [loadCount, setLoadCount] = useState(DEFAULT_LOAD_COUNT);
  const navigate = useNavigate();
  const location = useLocation();
  const { company } = useParams();
  const { permissions } = useAppUserDetailsFromJwt<Permissions>();
  const [companyNameSearch, setCompanyNameSearch] = useState('');
  const debouncedSearchTerm = useDebounce(companyNameSearch, 250);

  const [loadingSites, setLoadingSites] = useState(false);

  const [selected, setSelected] = useState(company);

  const { client, data, error, loading } = usePlutoApiQuery(
    GET_COMPANIES_AND_SITES,
    {
      variables: {
        filter: { name: { iLike: `%${debouncedSearchTerm}%` } },
        paging: {
          first: loadCount,
        },
      },
    },
  );

  useEffect(() => {
    if (data && Array.isArray(data.companies.edges)) {
      const sortedCompanies = [...data.companies.edges].sort((a, b) =>
        a.node.name.toLowerCase().localeCompare(b.node.name.toLowerCase()),
      );
      setDisplayedRecords(sortedCompanies);
    }
  }, [data]);

  const hasMoreRecords = !!data?.companies?.pageInfo?.hasNextPage;

  const handleSearchChange: React.ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    dynatraceTrackInstantAction(
      `${DYNATRACE_ACTIONS.COMPANY_SEARCH}_${event.target.value}`,
    );
    setCompanyNameSearch(event.target.value);
    setLoadCount(DEFAULT_LOAD_COUNT);
  };

  const handleInputClear = () => setCompanyNameSearch('');

  const handleChange = async (selectedCompany: string) => {
    setSelected(selectedCompany);
    const selectedCompanyData = data.companies.edges.find(
      (edge: CompanyListDataNode) => edge.node.id === selectedCompany,
    );
    setCompanyData(selectedCompanyData);
    await client.clearStore();

    dynatraceTrackInstantAction(DYNATRACE_ACTIONS.SWITCH_COMPANY);
    dynatraceTrackInstantAction(DYNATRACE_ACTIONS.VIEW_ALL_ALL_MARKETS);
    const noCompany = !company;

    setLoadingSites(true);
    await prefetchSites({
      client,
      companyId: selectedCompany,
      currentDate,
    }).finally(() => setLoadingSites(false));

    const defaultPath = getDefaultPathForUser(permissions);

    await navigate(
      generatePath('/:company/:path?', {
        company: selectedCompany,
        path: extractFirstSegmentFromUrl(location.pathname) || defaultPath,
      }),
    );

    // prevent close -> open -> close bug when selecting company for the first time on app load
    noCompany
      ? setCompanyModalClosed()
      : setTimeout(() => {
          setCompanyModalClosed();
        }, 100);
  };

  useEffect(() => {
    if (inputRef && displayedRecords?.length) {
      inputRef?.current?.focus({ preventScroll: true });
    }
  }, [displayedRecords?.length]);

  // prevent background scrolling when modal open
  useEffect(() => {
    // have to unset overflow both in the cleanup & in the conditional because the SDS modal does not unmount when closed
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (error) {
    return <ErrorNotice>Error fetching companies</ErrorNotice>;
  }

  return (
    <CompanyModalWrapper
      open
      onClose={() => {
        setCompanyModalClosed();
      }}
      header={{
        title: 'Select company',
      }}
      closeButton={!!company}
      contentScrollable={false}
      padding={false}
      centered={true}
      size={Sizes.Small}
      $showMask={!company}
      $appTheme={appTheme}
      focusTriggerAfterClose={true}
    >
      <CompanyListContext.Provider value={{ loadingSites, selected }}>
        <div style={{ margin: '16px 16px 0 16px' }}>
          <TextInput
            data-testid="company-selector-modal-search"
            placeholder="Quick search..."
            onChange={handleSearchChange}
            value={companyNameSearch}
            onClear={handleInputClear}
            clearable={true}
            size={Sizes.Large}
            autoFocus
            ref={inputRef}
            tabIndex={0}
          />
        </div>
        <CompanyListScrollContainer key={debouncedSearchTerm}>
          <CompanyListWrapper
            variants={companyListAnimation}
            initial="hidden"
            animate="visible"
            data-testid="company-list"
          >
            {!!displayedRecords && displayedRecords?.length > 0
              ? displayedRecords.map((company: CompanyListDataNode) => (
                  <CompanyListItem
                    key={`${company.node.id}-${debouncedSearchTerm}`}
                    onChange={handleChange}
                    company={company}
                  />
                ))
              : null}
            {loading ? (
              <CompanyLoaderWrapper>
                <Loading size={Sizes.Large} />
              </CompanyLoaderWrapper>
            ) : null}
            {!loading && !data?.companies?.edges?.length ? (
              <NoDataFound />
            ) : null}
          </CompanyListWrapper>
          <LoadMoreButtonContainer>
            {hasMoreRecords ? (
              <Button
                onClick={() => {
                  setLoadCount(loadCount + DEFAULT_LOAD_COUNT);
                }}
                variant={'outlined'}
                fullWidth
              >
                Load more
              </Button>
            ) : null}
          </LoadMoreButtonContainer>
        </CompanyListScrollContainer>
      </CompanyListContext.Provider>
    </CompanyModalWrapper>
  );
};

export default CompanySelectorModal;
